import { useState, useEffect, useRef, useCallback } from 'react';
import { API, SCHEDULE_POLL_INTERVAL } from '../config';
import { startJitteredPoll } from '../lib/pollJitter';
import { apiFetch } from '../deviceId';

/**
 * Poll /api/lightshow/schedule/:pipeName every 2s.
 * Pauses polling when `paused` is true.
 * Aborts in-flight requests before starting new ones.
 */
export function useSchedulePoller(pipeName, paused = false) {
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const abortRef = useRef(null);
  const lastJsonRef = useRef(null);

  const fetchSchedule = useCallback(async () => {
    if (!pipeName) return;

    // Abort any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await apiFetch(API.schedule(pipeName), {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Only update state if data actually changed (avoids re-render cascade)
      const json = JSON.stringify(data);
      if (json !== lastJsonRef.current) {
        lastJsonRef.current = json;
        setSchedule(data);
      }

      setError(null);
      setIsLoading(false);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setIsLoading(false);
      }
    }
  }, [pipeName]);

  useEffect(() => {
    if (!pipeName || paused) return;

    // Initial fetch
    fetchSchedule();

    // Jittered rather than a fixed interval (MM #73): every phone arms at
    // roughly the same moment, so a flat 2s interval turns 200k clients into
    // synchronised 2s pulses against CloudFront. Nobody can perceive the
    // difference — the show start comes from the synced clock, not from which
    // poll happens to see the flip.
    intervalRef.current = startJitteredPoll(fetchSchedule, SCHEDULE_POLL_INTERVAL);

    return () => {
      intervalRef.current?.();
      abortRef.current?.abort();
    };
  }, [pipeName, paused, fetchSchedule]);

  return { schedule, isLoading, error };
}
